import React, { useEffect, useState, useRef } from 'react';
import useScrollScale from '../hooks/useScrollScale';
import '../App.css';
import Header from './Header';
import hero_image from '../assets/HERO.png';
import cropped_hero from '../assets/cropped-hero.png';
import testimonials from '../assets/testimonials.png';
import { Link } from 'react-router-dom';
import demoVideo from '../assets/video.mp4';

function Home() {
    const scale = useScrollScale();
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const heroImageRef = useRef(null);

    useEffect(() => {
        const videoElement = document.querySelector('.demo-video');
        const playButton = document.querySelector('.play-button');
        const videoOverlay = document.querySelector('.video-overlay');
        const videoContainer = document.querySelector('.demo-video-container');
        
        if (videoElement && videoContainer) {
            // Disable native controls
            videoElement.controls = false;
            
            // Function to handle play/pause
            const togglePlayPause = () => {
                if (videoElement.paused) {
                    videoElement.play()
                        .then(() => {
                            if (videoOverlay) videoOverlay.style.opacity = '0';
                        })
                        .catch(error => {
                            console.error("Error playing video:", error);
                        });
                } else {
                    videoElement.pause();
                    if (videoOverlay) videoOverlay.style.opacity = '1';
                }
            };
            
            // Add click event to the video container
            videoContainer.addEventListener('click', togglePlayPause);
            
            // Still keep the play button functionality if it exists
            if (playButton) {
                playButton.addEventListener('click', (e) => {
                    e.stopPropagation(); // Prevent double triggering
                    togglePlayPause();
                });
            }
            
            // Update overlay when video plays/pauses naturally
            if (videoOverlay) {
                videoElement.addEventListener('play', () => {
                    videoOverlay.style.opacity = '0';
                });
                
                videoElement.addEventListener('pause', () => {
                    videoOverlay.style.opacity = '1';
                });
                
                // Show overlay when video ends
                videoElement.addEventListener('ended', () => {
                    videoOverlay.style.opacity = '1';
                });
            }
            
            // Cleanup function
            return () => {
                videoContainer.removeEventListener('click', togglePlayPause);
                if (playButton) {
                    playButton.removeEventListener('click', togglePlayPause);
                }
                if (videoOverlay) {
                    videoElement.removeEventListener('play', () => {});
                    videoElement.removeEventListener('pause', () => {});
                    videoElement.removeEventListener('ended', () => {});
                }
            };
        }
    }, []);

    // Add a resize listener to update windowWidth state
    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };
        
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);
    
    // Update the scroll scaling effect in HomePage.js
    useEffect(() => {
        const applyScrollScale = () => {
            if (windowWidth > 768 && heroImageRef.current) {
                // Calculate scale based on scroll position
                const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                const maxScroll = 500; // Maximum scroll distance to consider
                const minScale = 0.7; // Starting scale factor
                const maxScale = 0.9; // Maximum scale factor
                
                // Calculate scale factor (1.0 at top, increasing to 1.2 as you scroll)
                const scaleFactor = Math.min(maxScale, minScale + (scrollTop / maxScroll) * 0.2);
                
                // Apply the scale transform
                heroImageRef.current.style.setProperty('--scroll-scale', scaleFactor);
                heroImageRef.current.classList.add('scale-on-scroll');
            }
        };
        
        // Initial application
        applyScrollScale();
        
        // Add scroll event listener
        window.addEventListener('scroll', applyScrollScale);
        
        // Cleanup
        return () => {
            window.removeEventListener('scroll', applyScrollScale);
        };
    }, [windowWidth]);

    return (
        <div className="home-container">
            <Header />
            {/* Main Content */}
            <main className="main-content">
                <div className="hero-text" style={{
                    marginTop: windowWidth > 768 ? '100px' : '40px'
                }}>
                    <h1>The next generation of hiring happens here</h1>
                </div>
                
                <div className="hero-image-container" ref={heroImageRef}>
                    {windowWidth < 768 ? (
                        <img 
                            className="hero-image-small"
                            src={cropped_hero} 
                            alt="Quokka Platform" 
                        />
                    ) : (
                        <img 
                            className="hero-image-large"
                            src={hero_image} 
                            alt="Quokka Platform" 
                            style={{marginTop: '-140px'}}
                        />
                    )}
                </div>
            </main>
            
            <div className="text-sections">
                <div className="mission-statement">
                    <h2>
                        Matchmaking from heaven.<br></br>
                        <span style={{color: '#848484'}}>Unlock the potential of operational fitness.</span>
                    </h2>
                    <div className="testimonials-section">
                        <p>Backed by trusted SMEs</p>
                        <div className="testimonials-logos">
                            <img src={testimonials} alt="Featured in publications" />
                        </div>
                    </div>

                    <div className="demo-video-container" style={{marginTop: '80px'}}>
                        <div className="video-caption" style={{marginTop: '20px', marginBottom: '30px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center'}}>
                            <h4 style={{marginTop: '20px', fontSize: '28px'}}>See Quokka in Action</h4>
                            <p style={{textAlign: 'center', fontSize: '18px'}}>Watch our demo to see how Quokka matches candidates with companies based on operational fit.</p>
                        </div>
                        <div className="video-wrapper">
                            <video 
                                className="demo-video" 
                                controls
                                poster="/assets/video-poster.jpg"
                                preload="metadata"
                            >
                                <source src={demoVideo} type="video/mp4" />
                                Your browser does not support the video tag.
                            </video>
                            <div className="video-overlay">
                                <button className="play-button" aria-label="Play video">
                                    <svg viewBox="0 0 24 24" width="48" height="48" stroke="currentColor" strokeWidth="1.5" fill="none">
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <polygon points="10 8 16 12 10 16 10 8" fill="currentColor"></polygon>
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Steps Section - Updated structure for perfect centering */}
            <div className="steps-section">
                <div className="steps-section-inner">
                    <div className="steps-section-content">
                        <div className="steps-header">
                            <h2>Quokka works in three simple steps.</h2>
                            <p>And in as few as 30 minutes.</p>
                            <button 
                                className="cta-button" 
                                onClick={() => window.open('https://meetings.hubspot.com/david4497', '_blank')}
                            >
                                Schedule Demo
                            </button>
                        </div>
                        
                        <div className="steps-content">
                            <div className="steps-container">
                                <div className="step-card">
                                    <div className="step-number">01</div>
                                    <h3>Create a profile</h3>
                                    <p>Fill out a profile in less than 5 minutes on our dashboard, including your job description.</p>
                                </div>
                                
                                <div className="step-card">
                                    <div className="step-number">02</div>
                                    <h3>Complete our assessment</h3>
                                    <p>Our tailored assessment will help you find the best candidates for your team.</p>
                                </div>

                                <div className="step-card">
                                    <div className="step-number">03</div>
                                    <h3>Receive Recommendations</h3>
                                    <p>Our candidate pool is constantly growing, meaning we'll always recommend you new candidates.</p>
                                </div>
                            </div>
                            
                            {/* <div className="steps-container">
                                <div className="step-card">
                                    <div className="step-number">03</div>
                                    <h3>Receive Recommendations</h3>
                                    <p>Our candidate pool is constantly growing, meaning we'll always recommend you new candidates.</p>
                                </div>
                            </div> */}
                        </div>
                    </div>
                </div>
            </div>

            {/* Matchmaking from Heaven section */}
            <div className="matchmaking-section">
                <h2>Matchmaking from heaven.</h2>
                <p>Unlock the potential of operational fitness.</p>
                <style>
                    {`
                    @media (min-width: 768px) {
                        .matchmaking-section {
                            display: none;
                        }
                    }
                    `}
                </style>
            </div>
        </div>
    );
}

export default Home;


            {/* Feature Cards Section */}
            // <div className="feature-cards-container" style={{display: 'none'}}>
            //     <div className="feature-card blue-card">
            //         <div className="card-content">
            //             <h2>Experience Quokka in person.</h2>
            //             <Link to="/schedule-demo" className="card-link">
            //                 Visit a showroom →
            //             </Link>
            //         </div>
            //     </div>
                
            //     <div className="feature-card beige-card">
            //         <div className="card-content">
            //             <h2>Learn how we squeeze a whole house into your yard.</h2>
            //             <Link to="/how-it-works" className="card-link">
            //                 How it works →
            //             </Link>
            //         </div>
            //     </div>
            // </div>